import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import api from "../../api/api";
import styles from "./Calendario.module.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const TIPOS = {
  recebimento: { label: "Recebimento", icon: "↓", color: "green" },
  envio:       { label: "Envio",       icon: "↑", color: "blue" },
  agendamento: { label: "Agendamento", icon: "◷", color: "yellow" },
  reuniao:     { label: "Reunião",     icon: "◉", color: "purple" },
  outro:       { label: "Outro",       icon: "◆", color: "gray" },
};

const STATUS_LABELS = {
  pendente:   "Pendente",
  confirmado: "Confirmado",
  concluido:  "Concluído",
  cancelado:  "Cancelado",
};

const VIEWS = ["mes", "semana", "lista"];
const VIEW_LABELS = { mes: "Mês", semana: "Semana", lista: "Lista" };

const FORM_INICIAL = {
  titulo: "",
  descricao: "",
  tipo: "recebimento",
  data_inicio: "",
  hora_inicio: "09:00",
  data_fim: "",
  dia_todo: false,
  status: "pendente",
  observacoes: "",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, "0");

const toISO = (date, hora) => {
  if (!date) return null;
  return new Date(`${date}T${hora || "00:00"}:00`).toISOString();
};

const fmtDataHora = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtData = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// ─── API ──────────────────────────────────────────────────────────────────────
// Mesma instância axios de api/api.js — igual ao Entrada.jsx

const calendario = {
  getEventos: (params = {}) =>
    api.get("/calendario", { params }).then((r) => r.data),

  getProximos: (limite = 5) =>
    api.get("/calendario/proximos", { params: { limite } }).then((r) => r.data),

  criar: (body) =>
    api.post("/calendario", body).then((r) => r.data),

  atualizar: (id, body) =>
    api.put(`/calendario/${id}`, body).then((r) => r.data),

  atualizarStatus: (id, status) =>
    api.patch(`/calendario/${id}/status`, { status }).then((r) => r.data),

  deletar: (id) =>
    api.delete(`/calendario/${id}`).then((r) => r.data),
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useToast() {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const show = useCallback((msg, tipo = "success") => {
    clearTimeout(timerRef.current);
    setToast({ msg, tipo });
    timerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  return { toast, show };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const Toast = ({ toast }) => {
  if (!toast) return null;
  return (
    <div className={`${styles.toast} ${styles[`toast_${toast.tipo}`]}`}>
      {toast.msg}
    </div>
  );
};

const LoadingSpinner = () => (
  <div className={styles.loading}>
    <div className={styles.spinner} />
    <span>Carregando eventos...</span>
  </div>
);

const TipoBadge = ({ tipo }) => {
  const t = TIPOS[tipo] || TIPOS.outro;
  return (
    <span className={`${styles.tipoBadge} ${styles[`tipo_${t.color}`]}`}>
      <span className={styles.tipoIcon}>{t.icon}</span>
      {t.label}
    </span>
  );
};

const StatusBadge = ({ status }) => (
  <span className={`${styles.statusBadge} ${styles[`status_${status}`]}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

const TipoPill = ({ tipo, selected, onClick }) => {
  const t = TIPOS[tipo];
  return (
    <button
      type="button"
      className={`${styles.tipoPill} ${selected ? styles[`tipoPillSel_${t.color}`] : ""}`}
      onClick={() => onClick(tipo)}
    >
      {t.icon} {t.label}
    </button>
  );
};

const EventoForm = ({ form, onChange, onTipoChange, onSubmit, onCancel, editando, loading }) => (
  <form className={styles.form} onSubmit={onSubmit} noValidate>
    <div className={styles.formGroup}>
      <label className={styles.formLabel}>Título *</label>
      <input
        className={styles.formInput}
        placeholder="Ex: Recebimento fornecedor..."
        value={form.titulo}
        onChange={(e) => onChange("titulo", e.target.value)}
        required
        autoFocus
      />
    </div>

    <div className={styles.formGroup}>
      <label className={styles.formLabel}>Tipo</label>
      <div className={styles.tipoRow}>
        {Object.keys(TIPOS).map((t) => (
          <TipoPill key={t} tipo={t} selected={form.tipo === t} onClick={onTipoChange} />
        ))}
      </div>
    </div>

    <div className={styles.formRow}>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Data *</label>
        <input
          type="date"
          className={styles.formInput}
          value={form.data_inicio}
          onChange={(e) => onChange("data_inicio", e.target.value)}
          required
        />
      </div>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Hora</label>
        <input
          type="time"
          className={styles.formInput}
          value={form.hora_inicio}
          onChange={(e) => onChange("hora_inicio", e.target.value)}
          disabled={form.dia_todo}
        />
      </div>
    </div>

    <div className={styles.formGroup}>
      <label className={styles.formCheck}>
        <input
          type="checkbox"
          checked={form.dia_todo}
          onChange={(e) => onChange("dia_todo", e.target.checked)}
        />
        <span>Dia todo</span>
      </label>
    </div>

    <div className={styles.formGroup}>
      <label className={styles.formLabel}>Status</label>
      <select
        className={styles.formSelect}
        value={form.status}
        onChange={(e) => onChange("status", e.target.value)}
      >
        {Object.entries(STATUS_LABELS).map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    </div>

    <div className={styles.formGroup}>
      <label className={styles.formLabel}>Descrição</label>
      <textarea
        className={styles.formTextarea}
        placeholder="Detalhes do evento..."
        value={form.descricao}
        onChange={(e) => onChange("descricao", e.target.value)}
        rows={2}
      />
    </div>

    <div className={styles.formGroup}>
      <label className={styles.formLabel}>Observações</label>
      <textarea
        className={styles.formTextarea}
        placeholder="Notas adicionais..."
        value={form.observacoes}
        onChange={(e) => onChange("observacoes", e.target.value)}
        rows={2}
      />
    </div>

    <div className={styles.formActions}>
      <button type="button" className={styles.btnSecondary} onClick={onCancel}>
        Cancelar
      </button>
      <button type="submit" className={styles.btnPrimary} disabled={loading}>
        {loading ? "Salvando..." : editando ? "Salvar alterações" : "Criar evento"}
      </button>
    </div>
  </form>
);

const Sidebar = ({
  aberta, modoForm, eventoDetalhe,
  form, onChange, onTipoChange, onSubmit, onCancel,
  onAbrirForm, onFechar, onDeletar, onStatusRapido,
  loadingForm, proximos,
}) => (
  <aside className={`${styles.sidebar} ${aberta ? styles.sidebarOpen : ""}`}>
    <div className={styles.sidebarHead}>
      <h3 className={styles.sidebarTitle}>
        {modoForm
          ? form._id ? "Editar Evento" : "Novo Evento"
          : eventoDetalhe ? "Detalhes" : "Próximos Eventos"}
      </h3>
      <button className={styles.sidebarClose} onClick={onFechar} aria-label="Fechar">✕</button>
    </div>

    <div className={styles.sidebarBody}>
      {modoForm && (
        <EventoForm
          form={form} onChange={onChange} onTipoChange={onTipoChange}
          onSubmit={onSubmit} onCancel={onCancel}
          editando={Boolean(form._id)} loading={loadingForm}
        />
      )}

      {!modoForm && eventoDetalhe && (
        <div className={styles.detalhe}>
          <div className={styles.detalheHeader}>
            <TipoBadge tipo={eventoDetalhe.tipo} />
            <StatusBadge status={eventoDetalhe.status} />
          </div>
          <h4 className={styles.detalheTitulo}>{eventoDetalhe.titulo}</h4>

          <div className={styles.detalheFields}>
            <div className={styles.detalheField}>
              <span className={styles.detalheFieldIcon}>◷</span>
              <div>
                <span className={styles.detalheFieldLabel}>Data</span>
                <span className={styles.detalheFieldValue}>
                  {eventoDetalhe.dia_todo
                    ? fmtData(eventoDetalhe.data_inicio) + " (dia todo)"
                    : fmtDataHora(eventoDetalhe.data_inicio)}
                </span>
              </div>
            </div>

            {eventoDetalhe.usuario_nome && (
              <div className={styles.detalheField}>
                <span className={styles.detalheFieldIcon}>◉</span>
                <div>
                  <span className={styles.detalheFieldLabel}>Criado por</span>
                  <span className={styles.detalheFieldValue}>{eventoDetalhe.usuario_nome}</span>
                </div>
              </div>
            )}

            {eventoDetalhe.descricao && (
              <div className={styles.detalheField}>
                <span className={styles.detalheFieldIcon}>≡</span>
                <div>
                  <span className={styles.detalheFieldLabel}>Descrição</span>
                  <span className={styles.detalheFieldValue}>{eventoDetalhe.descricao}</span>
                </div>
              </div>
            )}

            {eventoDetalhe.observacoes && (
              <div className={styles.detalheField}>
                <span className={styles.detalheFieldIcon}>✎</span>
                <div>
                  <span className={styles.detalheFieldLabel}>Observações</span>
                  <span className={styles.detalheFieldValue}>{eventoDetalhe.observacoes}</span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.detalheStatusRow}>
            <span className={styles.detalheFieldLabel} style={{ marginBottom: 6 }}>
              Alterar status:
            </span>
            <div className={styles.statusBtns}>
              {Object.entries(STATUS_LABELS).map(([v, l]) => (
                <button
                  key={v}
                  className={`${styles.statusBtn} ${eventoDetalhe.status === v ? styles.statusBtnActive : ""}`}
                  onClick={() => onStatusRapido(eventoDetalhe.id, v)}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.detalheActions}>
            <button className={styles.btnPrimary} onClick={() => onAbrirForm(eventoDetalhe)}>Editar</button>
            <button className={styles.btnDanger}  onClick={() => onDeletar(eventoDetalhe.id)}>Remover</button>
          </div>
        </div>
      )}

      {!modoForm && !eventoDetalhe && (
        <div className={styles.proximosList}>
          {proximos.length === 0 ? (
            <p className={styles.emptyText}>Nenhum evento próximo.</p>
          ) : (
            proximos.map((ev) => (
              <div key={ev.id} className={styles.proximoItem}>
                <div className={`${styles.proximoDot} ${styles[`tipo_${TIPOS[ev.tipo]?.color || "gray"}`]}`} />
                <div className={styles.proximoInfo}>
                  <span className={styles.proximoTitulo}>{ev.titulo}</span>
                  <span className={styles.proximoData}>{fmtDataHora(ev.data_inicio)}</span>
                </div>
                <StatusBadge status={ev.status} />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  </aside>
);

const VistaMes = ({ ano, mes, eventos, filtros, onDiaClick, onEventoClick }) => {
  const hoje    = new Date();
  const primeiro = new Date(ano, mes, 1);
  const ultimo   = new Date(ano, mes + 1, 0);
  const inicio   = new Date(primeiro);
  inicio.setDate(1 - primeiro.getDay());

  const dias = [];
  const cur  = new Date(inicio);
  while (cur <= ultimo || cur.getDay() !== 0) {
    dias.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
    if (dias.length > 42) break;
  }

  const eventosFiltrados = eventos.filter((e) => filtros.has(e.tipo));
  const evsDia = (d) => eventosFiltrados.filter((e) => sameDay(new Date(e.data_inicio), d));

  return (
    <div className={styles.gridWrap}>
      <div className={styles.weekdays}>
        {WEEKDAYS.map((d) => <div key={d} className={styles.weekday}>{d}</div>)}
      </div>
      <div className={styles.days}>
        {dias.map((d, i) => {
          const isHoje  = sameDay(d, hoje);
          const isOther = d.getMonth() !== mes;
          const evs     = evsDia(d);
          const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          return (
            <div
              key={i}
              className={`${styles.day} ${isHoje ? styles.dayToday : ""} ${isOther ? styles.dayOther : ""}`}
              onClick={() => onDiaClick(dateStr)}
            >
              <div className={`${styles.dayNum} ${isHoje ? styles.dayNumToday : ""}`}>
                {d.getDate()}
              </div>
              <div className={styles.dayEvents}>
                {evs.slice(0, 3).map((ev) => (
                  <div
                    key={ev.id}
                    className={`${styles.dayEvent} ${styles[`tipoEvent_${TIPOS[ev.tipo]?.color || "gray"}`]} ${ev.status === "concluido" ? styles.dayEventDone : ""} ${ev.status === "cancelado" ? styles.dayEventCancel : ""}`}
                    onClick={(e) => { e.stopPropagation(); onEventoClick(ev); }}
                    title={ev.titulo}
                  >
                    {ev.titulo}
                  </div>
                ))}
                {evs.length > 3 && <div className={styles.dayMore}>+{evs.length - 3} mais</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const VistaSemana = ({ ano, mes, dia, eventos, filtros, onEventoClick }) => {
  const hoje   = new Date();
  const ref    = new Date(ano, mes, dia || hoje.getDate());
  const inicio = new Date(ref);
  inicio.setDate(ref.getDate() - ref.getDay());

  const dias = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(inicio);
    d.setDate(inicio.getDate() + i);
    return d;
  });

  const eventosFiltrados = eventos.filter((e) => filtros.has(e.tipo));

  return (
    <div className={styles.semanaWrap}>
      <div className={styles.semanaCabecalho}>
        {dias.map((d, i) => (
          <div key={i} className={`${styles.semanaCol} ${sameDay(d, hoje) ? styles.semanaColunaHoje : ""}`}>
            <span className={styles.semanaWd}>{WEEKDAYS[i]}</span>
            <span className={`${styles.semanaDn} ${sameDay(d, hoje) ? styles.semanaDnHoje : ""}`}>
              {d.getDate()}
            </span>
          </div>
        ))}
      </div>
      <div className={styles.semanaEventos}>
        {dias.map((d, i) => {
          const evs = eventosFiltrados.filter((e) => sameDay(new Date(e.data_inicio), d));
          return (
            <div key={i} className={`${styles.semanaColunaEvs} ${sameDay(d, hoje) ? styles.semanaColunaHoje : ""}`}>
              {evs.length === 0
                ? <div className={styles.semanaVazio} />
                : evs.map((ev) => (
                  <div
                    key={ev.id}
                    className={`${styles.semanaEvento} ${styles[`tipoEvent_${TIPOS[ev.tipo]?.color || "gray"}`]}`}
                    onClick={() => onEventoClick(ev)}
                  >
                    <span className={styles.semanaEventoHora}>
                      {ev.dia_todo
                        ? "dia todo"
                        : `${pad(new Date(ev.data_inicio).getHours())}:${pad(new Date(ev.data_inicio).getMinutes())}`}
                    </span>
                    <span className={styles.semanaEventoTitulo}>{ev.titulo}</span>
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const VistaLista = ({ eventos, filtros, onEventoClick }) => {
  const eventosFiltrados = eventos
    .filter((e) => filtros.has(e.tipo))
    .sort((a, b) => new Date(a.data_inicio) - new Date(b.data_inicio));

  if (eventosFiltrados.length === 0)
    return <div className={styles.emptyState}>Nenhum evento encontrado neste período.</div>;

  const grupos = {};
  eventosFiltrados.forEach((ev) => {
    const d     = new Date(ev.data_inicio);
    const key   = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const label = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    if (!grupos[key]) grupos[key] = { label, evs: [] };
    grupos[key].evs.push(ev);
  });

  return (
    <div className={styles.listaWrap}>
      {Object.entries(grupos).map(([key, { label, evs }]) => {
        const isHoje = sameDay(new Date(evs[0].data_inicio), new Date());
        return (
          <div key={key} className={styles.listaGrupo}>
            <div className={`${styles.listaDataLabel} ${isHoje ? styles.listaDataHoje : ""}`}>
              {isHoje ? "Hoje · " : ""}{label}
            </div>
            <div className={styles.listaEventos}>
              {evs.map((ev) => (
                <div
                  key={ev.id}
                  className={`${styles.listaEvento} ${styles[`listaBorder_${TIPOS[ev.tipo]?.color || "gray"}`]}`}
                  onClick={() => onEventoClick(ev)}
                >
                  <div className={styles.listaEventoLeft}>
                    <span className={styles.listaEventoHora}>
                      {ev.dia_todo
                        ? "Dia todo"
                        : `${pad(new Date(ev.data_inicio).getHours())}:${pad(new Date(ev.data_inicio).getMinutes())}`}
                    </span>
                    <span className={styles.listaEventoTitulo}>{ev.titulo}</span>
                    {ev.descricao && <span className={styles.listaEventoDesc}>{ev.descricao}</span>}
                  </div>
                  <div className={styles.listaEventoRight}>
                    <TipoBadge tipo={ev.tipo} />
                    <StatusBadge status={ev.status} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

const Calendario = () => {
  const { user } = useContext(AuthContext);
  const { toast, show: showToast } = useToast();

  const hoje = useMemo(() => new Date(), []);
  const [ano,     setAno]     = useState(hoje.getFullYear());
  const [mes,     setMes]     = useState(hoje.getMonth());
  const [diaSel,  setDiaSel]  = useState(hoje.getDate());
  const [view,    setView]    = useState("mes");

  const [eventos,     setEventos]     = useState([]);
  const [proximos,    setProximos]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [error,       setError]       = useState(null);

  const [filtros,       setFiltros]       = useState(new Set(Object.keys(TIPOS)));
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [modoForm,      setModoForm]      = useState(false);
  const [eventoDetalhe, setEventoDetalhe] = useState(null);
  const [form,          setForm]          = useState({ ...FORM_INICIAL });

  const carregarEventos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const inicio = new Date(ano, mes, 1).toISOString().slice(0, 10);
      const fim    = new Date(ano, mes + 1, 0).toISOString().slice(0, 10);
      const data   = await calendario.getEventos({ inicio, fim });
      setEventos(data);
    } catch (err) {
      setError(err.message);
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [ano, mes, showToast]);

  const carregarProximos = useCallback(async () => {
    try {
      const data = await calendario.getProximos(6);
      setProximos(data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { carregarEventos(); },  [carregarEventos]);
  useEffect(() => { carregarProximos(); }, [carregarProximos, eventos]);

  const navPrev = () => {
    if (view === "mes") {
      if (mes === 0) { setMes(11); setAno((a) => a - 1); } else setMes((m) => m - 1);
    } else {
      const prev = new Date(ano, mes, diaSel - 7);
      setAno(prev.getFullYear()); setMes(prev.getMonth()); setDiaSel(prev.getDate());
    }
  };

  const navNext = () => {
    if (view === "mes") {
      if (mes === 11) { setMes(0); setAno((a) => a + 1); } else setMes((m) => m + 1);
    } else {
      const next = new Date(ano, mes, diaSel + 7);
      setAno(next.getFullYear()); setMes(next.getMonth()); setDiaSel(next.getDate());
    }
  };

  const navHoje = () => { setAno(hoje.getFullYear()); setMes(hoje.getMonth()); setDiaSel(hoje.getDate()); };

  const abrirNovoEvento = (dateStr) => {
    setForm({ ...FORM_INICIAL, data_inicio: dateStr || "" });
    setEventoDetalhe(null); setModoForm(true); setSidebarAberta(true);
  };

  const abrirEditar = (ev) => {
    const d = new Date(ev.data_inicio);
    setForm({
      _id:         ev.id,
      titulo:      ev.titulo      || "",
      descricao:   ev.descricao   || "",
      tipo:        ev.tipo        || "outro",
      data_inicio: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      hora_inicio: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      data_fim:    ev.data_fim    || "",
      dia_todo:    ev.dia_todo    || false,
      status:      ev.status      || "pendente",
      observacoes: ev.observacoes || "",
    });
    setEventoDetalhe(null); setModoForm(true); setSidebarAberta(true);
  };

  const abrirDetalhe  = (ev) => { setEventoDetalhe(ev); setModoForm(false); setSidebarAberta(true); };
  const fecharSidebar = ()   => { setSidebarAberta(false); setModoForm(false); setEventoDetalhe(null); };
  const onFormChange  = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const onFormSubmit = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) { showToast("Informe o título", "error"); return; }
    if (!form.data_inicio)   { showToast("Informe a data",   "error"); return; }

    setLoadingForm(true);
    try {
      const payload = {
        titulo:      form.titulo.trim(),
        descricao:   form.descricao,
        tipo:        form.tipo,
        data_inicio: toISO(form.data_inicio, form.dia_todo ? "00:00" : form.hora_inicio),
        data_fim:    form.data_fim ? toISO(form.data_fim, "23:59") : null,
        dia_todo:    form.dia_todo,
        status:      form.status,
        observacoes: form.observacoes,
      };
      if (form._id) {
        await calendario.atualizar(form._id, payload);
        showToast("Evento atualizado!");
      } else {
        await calendario.criar(payload);
        showToast("Evento criado!");
      }
      await carregarEventos();
      fecharSidebar();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoadingForm(false);
    }
  };

  const onDeletar = async (id) => {
    if (!window.confirm("Remover este evento?")) return;
    try {
      await calendario.deletar(id);
      showToast("Evento removido.");
      await carregarEventos();
      fecharSidebar();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const onStatusRapido = async (id, status) => {
    try {
      const atualizado = await calendario.atualizarStatus(id, status);
      setEventoDetalhe(atualizado);
      setEventos((evs) => evs.map((e) => (e.id === id ? atualizado : e)));
      showToast("Status atualizado!");
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const toggleFiltro = (tipo) =>
    setFiltros((prev) => {
      const next = new Set(prev);
      next.has(tipo) ? next.delete(tipo) : next.add(tipo);
      return next;
    });

  const periodoLabel = useMemo(() => {
    if (view === "mes") return `${MONTHS[mes]} ${ano}`;
    if (view === "semana") {
      const ref = new Date(ano, mes, diaSel);
      const ini = new Date(ref); ini.setDate(ref.getDate() - ref.getDay());
      const fim = new Date(ini); fim.setDate(ini.getDate() + 6);
      return `${pad(ini.getDate())}/${pad(ini.getMonth() + 1)} – ${pad(fim.getDate())}/${pad(fim.getMonth() + 1)}/${ano}`;
    }
    return `${MONTHS[mes]} ${ano}`;
  }, [view, mes, ano, diaSel]);

  return (
    <div className={styles.root}>
      <Toast toast={toast} />

      <div className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <div className={styles.titleBlock}>
            <h1 className={styles.pageTitle}>Calendário</h1>
            <span className={styles.pageSubtitle}>Eventos e agendamentos</span>
          </div>
          <div className={styles.navGroup}>
            <button className={styles.navBtn} onClick={navPrev} aria-label="Anterior">‹</button>
            <span className={styles.periodoLabel}>{periodoLabel}</span>
            <button className={styles.navBtn} onClick={navNext} aria-label="Próximo">›</button>
          </div>
          <button className={styles.btnHoje} onClick={navHoje}>Hoje</button>
        </div>
        <div className={styles.topbarRight}>
          <div className={styles.viewToggle}>
            {VIEWS.map((v) => (
              <button
                key={v}
                className={`${styles.viewBtn} ${view === v ? styles.viewBtnActive : ""}`}
                onClick={() => setView(v)}
              >
                {VIEW_LABELS[v]}
              </button>
            ))}
          </div>
          <button className={styles.btnNovo} onClick={() => abrirNovoEvento("")}>
            + Novo Evento
          </button>
        </div>
      </div>

      <div className={styles.filtros}>
        <span className={styles.filtrosLabel}>Tipo:</span>
        {Object.entries(TIPOS).map(([tipo, { label, color }]) => (
          <button
            key={tipo}
            className={`${styles.filterChip} ${filtros.has(tipo) ? styles[`filterChipActive_${color}`] : ""}`}
            onClick={() => toggleFiltro(tipo)}
          >
            <span className={`${styles.filterDot} ${styles[`tipo_${color}`]}`} />
            {label}
          </button>
        ))}
        {error && <span className={styles.errorMsg}>{error}</span>}
      </div>

      <div className={styles.body}>
        <div className={styles.mainArea}>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              {view === "mes" && (
                <VistaMes
                  ano={ano} mes={mes} eventos={eventos} filtros={filtros}
                  onDiaClick={abrirNovoEvento} onEventoClick={abrirDetalhe}
                />
              )}
              {view === "semana" && (
                <VistaSemana
                  ano={ano} mes={mes} dia={diaSel} eventos={eventos} filtros={filtros}
                  onEventoClick={abrirDetalhe}
                />
              )}
              {view === "lista" && (
                <VistaLista eventos={eventos} filtros={filtros} onEventoClick={abrirDetalhe} />
              )}
            </>
          )}
        </div>

        <Sidebar
          aberta={sidebarAberta} modoForm={modoForm} eventoDetalhe={eventoDetalhe}
          form={form} onChange={onFormChange} onTipoChange={(t) => onFormChange("tipo", t)}
          onSubmit={onFormSubmit} onCancel={fecharSidebar}
          onAbrirForm={abrirEditar} onFechar={fecharSidebar}
          onDeletar={onDeletar} onStatusRapido={onStatusRapido}
          loadingForm={loadingForm} proximos={proximos}
        />
      </div>

      {sidebarAberta && <div className={styles.mobileOverlay} onClick={fecharSidebar} />}
    </div>
  );
};

export default Calendario;